import React, { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { formatRoleList } from "../utils/roleUtils";
import {
  Users,
  UsersRound,
  DollarSign,
  Award,
  BarChart3,
  LayoutDashboard,
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  FilePlus,
  History as HistoryIcon,
  CreditCard,
  Landmark,
  Settings,
  Building2,
  Wallet,
  Coins,
  ClipboardList,
  Clock,
  Calendar,
  CalendarCheck,
  CalendarClock,
  Smartphone,
  Network,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { canReachReports, canStartAnyReport } from "../utils/reportPermissions";
import Monogram from "../assets/monogram.svg";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const mainRef = useRef(null);
  // Set by a nav click that closes the open drawer, consumed once the new route
  // has rendered.
  const focusMainAfterNav = useRef(false);
  const { user, logout, hasRole, userPermissions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Read off userPermissions like every gate here, in the (resource, action) shape the
  // shared permission helpers take.
  const holds = (resource, action) =>
    userPermissions.some(p => p.resource === resource && p.action === action);

  // Helper to check if user has any relevant permission for a section
  const hasAnySectionPermission = (section) => {
    switch (section) {
      case 'Employees':
        return userPermissions.some(p => p.resource === 'employees');
      case 'Org Chart':
        return userPermissions.some(p => p.resource === 'employees');
      case 'Salaries':
        return userPermissions.some(p => p.resource === 'salaries');
      case 'Appraisals':
        return userPermissions.some(p => p.resource === 'appraisals');
      case 'Reports':
        return userPermissions.some(p => p.resource === 'reports');
      // Every report carries pay, so both need salaries:read_all on top of the reports grant
      // (the server answers 403 otherwise); New Report also covers the employee export.
      case 'New Report':
        return canStartAnyReport(holds);
      case 'History':
        return canReachReports(holds);
      case 'Users & Roles':
        return userPermissions.some(p => p.resource === 'users' || p.resource === 'roles');
      case 'Leaves':
        return userPermissions.some(p => p.resource === 'leaves');
      case 'Attendance':
        return userPermissions.some(p => p.resource === 'attendance'
          && ['read_all', 'read_department'].includes(p.action));
      case 'My Attendance':
        return userPermissions.some(p => p.resource === 'attendance' && p.action === 'read_own');
      case 'Mobile devices':
        return userPermissions.some(p => p.resource === 'devices' && p.action === 'manage');
      case 'Transaction Fees':
        return userPermissions.some(p => p.resource === 'system');
      case 'Onboarding':
        return userPermissions.some(p => p.resource === 'onboarding' && ['read_all', 'read_department', 'review'].includes(p.action));
      default:
        return true; // Dashboard always accessible
    }
  };

  const superAdmin = Boolean(hasRole && hasRole('super_admin'));

  // Top-level entries are either a link ({ name, href, icon }) or a collapsible
  // group ({ name, icon, items }). Group icons are deliberately distinct from the
  // icons of the items they hold, so a collapsed header never mirrors a child row.
  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    {
      name: 'People',
      icon: UsersRound,
      items: [
        { name: 'Employees', href: '/employees', icon: Users },
        { name: 'Org Chart', href: '/org-chart', icon: Network },
        { name: 'Onboarding', href: '/onboarding/requests', icon: ClipboardList },
        // Documents have no page of their own: they live in each employee's profile.
        { name: 'Appraisals', href: '/appraisals', icon: Award }
      ]
    },
    {
      name: 'Time',
      icon: Clock,
      items: [
        { name: 'Leaves', href: '/leaves', icon: Calendar },
        { name: 'Attendance', href: '/attendance', icon: CalendarCheck },
        { name: 'My Attendance', href: '/my-attendance', icon: CalendarClock },
        { name: 'Mobile devices', href: '/mobile-devices', icon: Smartphone }
      ]
    },
    {
      name: 'Payroll',
      icon: Wallet,
      items: [
        { name: 'Salaries', href: '/salaries', icon: DollarSign },
        { name: 'Transaction Fees', href: '/transaction-fees', icon: CreditCard },
        // Payment Methods and Currencies stay super-admin-only configuration.
        ...(superAdmin ? [
          { name: 'Payment Methods', href: '/payment-methods', icon: Landmark },
          { name: 'Currencies', href: '/currencies', icon: Coins }
        ] : [])
      ]
    },
    {
      name: 'Reports',
      icon: BarChart3,
      items: [
        { name: 'New Report', href: '/reports/new', icon: FilePlus },
        { name: 'History', href: '/reports', icon: HistoryIcon }
      ]
    },
    {
      name: 'Admin',
      icon: Settings,
      items: [
        { name: 'Users & Roles', href: '/users', icon: Users },
        ...(superAdmin ? [{ name: 'Departments', href: '/departments', icon: Building2 }] : [])
      ]
    }
  ];

  // Derive a stable kebab-case slug from a nav route for data-testid targeting.
  // e.g. "/" -> "dashboard", "/employees" -> "employees",
  // "/onboarding/requests" -> "onboarding-requests", "/reports/new" -> "reports-new"
  const navSlug = (href) => {
    if (!href || href === '/') return 'dashboard';
    return href
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
      .replace(/\//g, '-');
  };

  // Group headers get their own testid namespace: 'People' -> nav-group-people.
  const groupSlug = (name) => name.toLowerCase().replace(/\s+/g, '-');

  // A route belongs to an item when it is that item's href or a child of it, so
  // /employees/5 still belongs to Employees. Dashboard's '/' is a prefix of every
  // route, so it only ever matches exactly.
  const routeMatches = (href) => (
    href === '/'
      ? location.pathname === '/'
      : location.pathname === href || location.pathname.startsWith(href + '/')
  );

  const activeGroup = navigation.find(
    entry => entry.items && entry.items.some(item => routeMatches(item.href))
  )?.name;

  const [openGroups, setOpenGroups] = useState(() => (activeGroup ? { [activeGroup]: true } : {}));
  const [toggledGroups, setToggledGroups] = useState({});

  // Auto-expand the group owning the current route, unless the user has toggled
  // that group by hand - from then on their choice wins for that group only.
  useEffect(() => {
    if (activeGroup && !toggledGroups[activeGroup]) {
      setOpenGroups(prev => (prev[activeGroup] ? prev : { ...prev, [activeGroup]: true }));
    }
  }, [activeGroup, toggledGroups]);

  // Below lg a nav click closes the drawer, which then goes invisible and would
  // drop the focused link's focus to <body>. Only an open drawer arms the focus
  // move: at lg and up it is never opened, so a desktop click keeps its focus.
  const goTo = (href) => {
    if (sidebarOpen) focusMainAfterNav.current = true;
    navigate(href);
    setSidebarOpen(false);
  };

  // Runs once the new location has rendered, so focus lands on the new page's
  // content. It never takes focus from somewhere else: only from the drawer, or
  // from <body> if the browser already dropped it there.
  useEffect(() => {
    if (!focusMainAfterNav.current) return;
    focusMainAfterNav.current = false;
    const active = document.activeElement;
    if (!active || active === document.body || drawerRef.current?.contains(active)) {
      // preventScroll: below lg <main> starts under the fixed 64px header, so a
      // plain focus() on a page already taller than the viewport scrolls it up
      // and puts the page title under the header.
      mainRef.current?.focus({ preventScroll: true });
    }
  }, [location]);

  const toggleGroup = (name) => {
    setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }));
    setToggledGroups(prev => ({ ...prev, [name]: true }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center">
            <div className="w-8 h-8 flex items-center justify-center">
              <img
                src={Monogram}
                alt="ByteCorp Monogram"
                className="w-8 h-8 object-contain"
              />
            </div>
            <h1 className="ml-2 text-xl font-semibold text-gray-900">
              ByteCorp HR
            </h1>
          </div>
          <button
            ref={menuButtonRef}
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Open menu"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Sidebar. Below lg the closed drawer is `invisible`, which takes its links
          out of the Tab order and the accessibility tree; `visibility` is in the
          transition list so it only flips once the slide-out has finished (and
          flips back at once on open). lg:visible keeps it usable at lg and up. */}
      <aside
        ref={drawerRef}
        id="app-sidebar"
        className={`fixed top-0 bottom-0 left-0 z-40 w-64 bg-white transform transition-[transform,box-shadow,visibility] duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0 shadow-lg" : "-translate-x-full invisible"}
          lg:translate-x-0 lg:shadow-lg lg:visible`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 shrink-0">
            <div className="flex items-center">
              <div className="w-8 h-8 flex items-center justify-center">
                <img
                  src={Monogram}
                  alt="ByteCorp Monogram"
                  className="w-8 h-8 object-contain"
                />
              </div>
              <h1 className="ml-2 text-xl font-semibold text-gray-900">
                ByteCorp HR
              </h1>
            </div>
            <button
              onClick={() => {
                setSidebarOpen(false);
                // The drawer is about to go invisible; put focus back where the
                // user opened it instead of letting it drop to <body>.
                menuButtonRef.current?.focus();
              }}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-6 px-3">
            {user?.employee_id === null && (
              <div className="mb-3">
                <button
                  onClick={() => goTo('/onboarding')}
                  className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md bg-warning-50 text-warning-800 hover:bg-warning-100 border border-warning-200"
                >
                  <ClipboardList size={18} className="mr-2 text-warning-600 shrink-0" />
                  Complete Onboarding
                </button>
              </div>
            )}
            <div className="space-y-1">
              {navigation
                .filter(entry => hasAnySectionPermission(entry.name))
                .map((entry) => {
                  const Icon = entry.icon;

                  // Top-level link (Dashboard).
                  if (!entry.items) {
                    return (
                      <button
                        key={entry.name}
                        onClick={() => goTo(entry.href)}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          location.pathname === entry.href
                            ? "bg-primary-100 text-primary-700 border-r-2 border-primary-600"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                        data-testid={`nav-${navSlug(entry.href)}`}
                      >
                        <Icon size={20} className="mr-3" />
                        {entry.name}
                      </button>
                    );
                  }

                  const items = entry.items.filter(item => hasAnySectionPermission(item.name));
                  if (items.length === 0) return null;

                  const open = Boolean(openGroups[entry.name]);
                  // A collapsed group hides the active item's highlight, so tint the
                  // header instead. An open group stays neutral - the item shows it.
                  const tinted = !open && entry.name === activeGroup;

                  return (
                    <div key={entry.name}>
                      <button
                        onClick={() => toggleGroup(entry.name)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          tinted
                            ? "text-primary-700 hover:bg-gray-100"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                        aria-expanded={open}
                        data-testid={`nav-group-${groupSlug(entry.name)}`}
                      >
                        <span className="flex items-center">
                          <Icon size={20} className="mr-3" />
                          {entry.name}
                        </span>
                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                      {open && (
                        <div className="ml-6 mt-1 space-y-1">
                          {items.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <button
                                key={item.name}
                                onClick={() => goTo(item.href)}
                                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                  location.pathname === item.href
                                    ? "bg-primary-50 text-primary-600"
                                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                }`}
                                data-testid={`nav-${navSlug(item.href)}`}
                              >
                                <ItemIcon size={16} className="mr-3" />
                                {item.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </nav>

          {/* Profile + Logout */}
          <div className="p-4 border-t border-gray-200 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  type="button"
                  className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary-400"
                  onClick={() => goTo("/profile")}
                  title="My Profile"
                >
                  <User size={16} className="text-gray-600" />
                </button>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.first_name || user?.username}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatRoleList(user?.roles, '-')}
                  </p>
                  <p className="text-xs text-primary-600 mt-1">
                    Welcome back!
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="relative lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        {/* tabIndex -1: where a closing popup puts focus when the button that
            opened it is gone (a deleted row), instead of dropping it on <body>,
            and where a nav click in the mobile drawer lands (see goTo). */}
        <main ref={mainRef} tabIndex={-1} className="p-4 sm:p-6 lg:p-8 focus:outline-none">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
